/**
 * Phase J.2 Pass 3 — Admin payments list page.
 *
 * Mirrors AdminInvoicesListPage's filter pattern: URL-persisted state,
 * single inline filter row + table. Status badges, last-error tooltip,
 * masked PI id with copy-on-click, deep-link to invoice detail.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    CreditCard, Search, ChevronRight, ArrowUpDown, AlertTriangle, Copy, Calendar, DollarSign,
} from "lucide-react";

const STATUS_OPTIONS = [
    { value: "succeeded", label: "Succeeded", color: "bg-emerald-100 text-emerald-900 ring-emerald-200" },
    { value: "processing", label: "Processing", color: "bg-amber-50 text-amber-900 ring-amber-200" },
    { value: "requires_action", label: "Requires action", color: "bg-amber-50 text-amber-900 ring-amber-200" },
    { value: "requires_confirmation", label: "Requires confirmation", color: "bg-amber-50 text-amber-900 ring-amber-200" },
    { value: "requires_payment_method", label: "Requires bank", color: "bg-slate-100 text-slate-700 ring-slate-200" },
    { value: "failed", label: "Failed", color: "bg-red-50 text-red-700 ring-red-200" },
    { value: "canceled", label: "Canceled", color: "bg-red-50 text-red-700 ring-red-200" },
    { value: "refunded", label: "Refunded", color: "bg-purple-100 text-purple-900 ring-purple-200" },
    { value: "partially_refunded", label: "Partial refund", color: "bg-purple-100 text-purple-900 ring-purple-200" },
];

const SORT_OPTIONS = [
    { value: "-created_at", label: "Newest first" },
    { value: "created_at", label: "Oldest first" },
    { value: "-amount_cents", label: "Amount ↓" },
    { value: "amount_cents", label: "Amount ↑" },
    { value: "-status", label: "Status (Z–A)" },
    { value: "status", label: "Status (A–Z)" },
];

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtRelTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
}

function StatusBadge({ status, lastError }) {
    const o = STATUS_OPTIONS.find((x) => x.value === status) || {
        value: status, label: status, color: "bg-slate-100 text-slate-700",
    };
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${o.color}`}
            data-testid={`payment-status-badge-${o.value}`}
            title={lastError || o.label}
        >
            {o.label}
            {lastError && <AlertTriangle size={10} className="text-red-700" />}
        </span>
    );
}

function PIChip({ piId }) {
    const masked = piId ? `••••${piId.slice(-8)}` : "—";
    const copy = () => {
        navigator.clipboard.writeText(piId || "");
        toast.success("Copied to clipboard");
    };
    return (
        <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 font-mono text-xs text-slate-700 hover:text-blue-900"
            title={piId || ""}
            data-testid="payment-pi-id"
        >
            {masked} <Copy size={10} className="opacity-50" />
        </button>
    );
}

export default function AdminPaymentsListPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const status = searchParams.get("status") || "";
    const sort = searchParams.get("sort") || "-created_at";
    const fromDate = searchParams.get("from") || "";
    const toDate = searchParams.get("to") || "";
    const minAmt = searchParams.get("min_amount") || "";
    const maxAmt = searchParams.get("max_amount") || "";
    const [q, setQ] = useState(searchParams.get("q") || "");
    const skip = parseInt(searchParams.get("skip") || "0", 10);
    const limit = parseInt(searchParams.get("limit") || "25", 10);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === "" || value == null) next.delete(key);
        else next.set(key, value);
        if (key !== "skip") next.delete("skip");
        setSearchParams(next, { replace: true });
    };

    const toggleStatus = (s) => {
        const current = new Set((status || "").split(",").filter(Boolean));
        if (current.has(s)) current.delete(s); else current.add(s);
        updateParam("status", [...current].join(","));
    };

    const selectedStatuses = useMemo(
        () => new Set((status || "").split(",").filter(Boolean)),
        [status],
    );

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (status) params.set("status", status);
            if (sort) params.set("sort", sort);
            if (fromDate) params.set("from", fromDate);
            if (toDate) params.set("to", toDate);
            if (minAmt) params.set("min_amount", minAmt);
            if (maxAmt) params.set("max_amount", maxAmt);
            if (q) params.set("q", q);
            params.set("skip", String(skip));
            params.set("limit", String(limit));
            const { data } = await api.get(`/admin/payments?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ },
        [status, sort, fromDate, toDate, minAmt, maxAmt, skip, limit]);

    return (
        <DashboardShell>
            <div className="fade-up">
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                    Payments
                </p>
                <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
                    Stripe payments
                </h1>
                <p className="mt-2 text-sm text-slate-500">
                    All ACH payment intents we've minted and their settled status. Click an invoice number to open its full timeline.
                </p>
            </div>

            <Card className="mt-6">
                <CardContent className="p-4 space-y-3">
                    {/* Filter chip row */}
                    <div className="flex flex-wrap gap-1.5" data-testid="payments-status-filters">
                        {STATUS_OPTIONS.map((s) => {
                            const on = selectedStatuses.has(s.value);
                            return (
                                <button
                                    key={s.value}
                                    onClick={() => toggleStatus(s.value)}
                                    data-testid={`payments-status-chip-${s.value}`}
                                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 transition-colors ${
                                        on ? s.color : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"
                                    }`}
                                >
                                    {s.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 pt-1">
                        <form
                            onSubmit={(e) => { e.preventDefault(); updateParam("q", q); }}
                            className="sm:col-span-2 lg:col-span-4 relative"
                            data-testid="payments-search-form"
                        >
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search PI id or invoice #"
                                className="pl-9 h-10"
                                data-testid="payments-search-input"
                            />
                        </form>

                        <div className="lg:col-span-3 flex items-center gap-2 text-xs text-slate-500 min-w-0" data-testid="payments-date-range">
                            <Calendar size={14} className="shrink-0" />
                            <Input
                                type="date" value={fromDate.slice(0, 10)}
                                onChange={(e) => updateParam("from", e.target.value)}
                                className="h-9 text-xs flex-1 min-w-0"
                                data-testid="payments-from-date"
                            />
                            <span className="shrink-0">–</span>
                            <Input
                                type="date" value={toDate.slice(0, 10)}
                                onChange={(e) => updateParam("to", e.target.value)}
                                className="h-9 text-xs flex-1 min-w-0"
                                data-testid="payments-to-date"
                            />
                        </div>
                        <div className="lg:col-span-3 flex items-center gap-2 text-xs text-slate-500 min-w-0" data-testid="payments-amount-range">
                            <DollarSign size={14} className="shrink-0" />
                            <Input type="number" min="0" step="1" placeholder="min"
                                value={minAmt}
                                onChange={(e) => updateParam("min_amount", e.target.value)}
                                className="h-9 text-xs flex-1 min-w-0"
                                data-testid="payments-min-amount"
                            />
                            <span className="shrink-0">–</span>
                            <Input type="number" min="0" step="1" placeholder="max"
                                value={maxAmt}
                                onChange={(e) => updateParam("max_amount", e.target.value)}
                                className="h-9 text-xs flex-1 min-w-0"
                                data-testid="payments-max-amount"
                            />
                        </div>
                        <div className="lg:col-span-2 min-w-0" data-testid="payments-sort">
                            <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
                                <SelectTrigger className="h-10 gap-1.5">
                                    <ArrowUpDown size={14} /> <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SORT_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="mt-6">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6 space-y-3" data-testid="payments-loading">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />
                            ))}
                        </div>
                    ) : items.length === 0 ? (
                        <div className="p-10 text-center text-sm text-slate-400 italic"
                             data-testid="payments-empty">
                            <CreditCard size={28} className="mx-auto text-slate-300 mb-2" />
                            No payments yet matching these filters.
                        </div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="text-xs text-slate-500 uppercase border-b border-slate-100 bg-slate-50/40">
                                <tr>
                                    <th className="text-left py-2 px-4">Status</th>
                                    <th className="text-left py-2 px-4">PI id</th>
                                    <th className="text-left py-2 px-4">Invoice</th>
                                    <th className="text-left py-2 px-4">Customer</th>
                                    <th className="text-right py-2 px-4">Amount</th>
                                    <th className="text-left py-2 px-4">Created</th>
                                    <th className="text-right py-2 px-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((p) => {
                                    const inv = p.invoice;
                                    const partRef = p.refunded_cents_total > 0
                                        && p.refunded_cents_total < (p.amount_cents || 0);
                                    return (
                                        <tr key={p.id} data-testid={`payments-row-${p.id}`}>
                                            <td className="py-2 px-4">
                                                <StatusBadge
                                                    status={p.status}
                                                    lastError={p.last_payment_error_message}
                                                />
                                                {p.consumed && (
                                                    <span className="ml-1 text-[10px] text-slate-400">consumed</span>
                                                )}
                                            </td>
                                            <td className="py-2 px-4"><PIChip piId={p.stripe_payment_intent_id} /></td>
                                            <td className="py-2 px-4">
                                                {inv ? (
                                                    <Link to={`/admin/invoices/${inv.id}`}
                                                          className="text-blue-900 hover:underline font-medium"
                                                          data-testid={`payments-invoice-link-${p.id}`}>
                                                        {inv.number}
                                                    </Link>
                                                ) : <span className="text-slate-400">—</span>}
                                            </td>
                                            <td className="py-2 px-4 text-slate-700">
                                                {inv?.customer_org_name && <div className="font-medium text-slate-900">{inv.customer_org_name}</div>}
                                                {inv?.customer_email && <div className="text-xs text-slate-500">{inv.customer_email}</div>}
                                            </td>
                                            <td className="py-2 px-4 text-right tabular-nums">
                                                <div className="font-semibold text-slate-900">{fmtMoney(p.amount_cents)}</div>
                                                {partRef && (
                                                    <div className="text-xs text-purple-700">
                                                        net {fmtMoney((p.amount_cents || 0) - p.refunded_cents_total)}
                                                        {" · "}refunded {fmtMoney(p.refunded_cents_total)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-2 px-4 text-slate-600" title={p.created_at}>
                                                {fmtRelTime(p.created_at)}
                                            </td>
                                            <td className="py-2 px-4 text-right">
                                                {inv && (
                                                    <Button asChild size="sm" variant="ghost" className="gap-1">
                                                        <Link to={`/admin/invoices/${inv.id}#payments`}
                                                              data-testid={`payments-view-${p.id}`}>
                                                            View <ChevronRight size={12} />
                                                        </Link>
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {total > limit && (
                <div className="mt-4 flex items-center justify-between text-sm text-slate-500"
                     data-testid="payments-pagination">
                    <span>Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
                    <div className="flex gap-2">
                        <Button
                            variant="outline" size="sm"
                            disabled={skip === 0}
                            onClick={() => updateParam("skip", String(Math.max(0, skip - limit)))}
                            data-testid="payments-prev-page"
                        >
                            Prev
                        </Button>
                        <Button
                            variant="outline" size="sm"
                            disabled={skip + limit >= total}
                            onClick={() => updateParam("skip", String(skip + limit))}
                            data-testid="payments-next-page"
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </DashboardShell>
    );
}
